import React, { createContext, useContext, useEffect, useState } from 'react';

const CartContext = createContext(null);

const readStorage = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(() => readStorage('andobree_cart', []));
  const [wishlist, setWishlist] = useState(() => readStorage('andobree_wishlist', []));
  const [cartOpen, setCartOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem('andobree_cart', JSON.stringify(cart));
  }, [cart]);
  useEffect(() => {
    localStorage.setItem('andobree_wishlist', JSON.stringify(wishlist));
  }, [wishlist]);

  const MAX_PER_PRODUCT = 25;

  const addToCart = (item) => {
    setCart((prev) => {
      const key = `${item.id}-${item.size}-${item.color}`;
      const totalForProduct = prev
        .filter((i) => i.id === item.id)
        .reduce((sum, i) => sum + i.quantity, 0);
      const remaining = MAX_PER_PRODUCT - totalForProduct;
      if (remaining <= 0) return prev;
      const addQty = Math.min(item.quantity, remaining);
      const existing = prev.find((i) => `${i.id}-${i.size}-${i.color}` === key);
      if (existing) {
        return prev.map((i) =>
          `${i.id}-${i.size}-${i.color}` === key
            ? { ...i, quantity: i.quantity + addQty }
            : i
        );
      }
      return [...prev, { ...item, quantity: addQty }];
    });
    setCartOpen(true);
  };

  const removeFromCart = (key) => {
    setCart((prev) => prev.filter((i) => `${i.id}-${i.size}-${i.color}` !== key));
  };

  const updateQuantity = (key, qty) => {
    if (qty < 1) return;
    setCart((prev) => {
      const target = prev.find((i) => `${i.id}-${i.size}-${i.color}` === key);
      if (!target) return prev;
      const totalForProductOther = prev
        .filter((i) => i.id === target.id && `${i.id}-${i.size}-${i.color}` !== key)
        .reduce((sum, i) => sum + i.quantity, 0);
      const cappedQty = Math.min(qty, MAX_PER_PRODUCT - totalForProductOther);
      if (cappedQty < 1) return prev;
      return prev.map((i) =>
        `${i.id}-${i.size}-${i.color}` === key ? { ...i, quantity: cappedQty } : i
      );
    });
  };

  const toggleWishlist = (productId) => {
    setWishlist((prev) =>
      prev.includes(productId) ? prev.filter((id) => id !== productId) : [...prev, productId]
    );
  };

  const clearCart = () => setCart([]);

  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);
  const cartTotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);

  return (
    <CartContext.Provider
      value={{
        cart,
        wishlist,
        cartOpen,
        setCartOpen,
        addToCart,
        removeFromCart,
        updateQuantity,
        toggleWishlist,
        clearCart,
        cartCount,
        cartTotal,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
};
